import dayjs from 'dayjs'
// vitest からは Nuxt auto-import が効かないため明示 import に。
import { ref, readonly, watchEffect, type Ref } from 'vue'
import { mergeById } from '~/utils/merge-by-id'
import { storage } from '~/utils/session-storage'
import {
  getOrderAttachedFile,
  getOrderAttachedFileByToken,
  getOrderByToken,
  getOrderDetail,
  getOrderReceiptFile,
  linkOrder,
  searchOrders,
} from './api/order'
import { useSubProfile } from './sub-profile'
import { useSubscriptionRequest, type SubscriptionRequestBase } from './subscription-request'
import type {
  GetOrder200,
  GetOrderByToken200,
  GetOrderList200,
  GetOrderListQueryParams,
} from '~/types/api/Order'
import type { CalculationBase } from '~/types/api/_atoms/CalculationBase'
import type { Method } from '~/types/api/_atoms/Method'
import type { MethodForOrders } from '~/types/api/_atoms/MethodForOrders'
import type { CamelCaseKeys } from '~/types/util'

export type OrderPaymentDetailMethod = MethodForOrders
export type OrderLoanApplicationDetailMethod = Method
export type OrderCalculationBase = CalculationBase

export const MAX_RECEIPT_FILE_DOWNLOAD_COUNT = 10

// 受診者(対象者)は生成型の proxyGroupMemberId で表す。proxy_group_member_id は serializer の
// 無条件属性なので issued_user_data の指定に依らず返る。
export type OrderBase = CamelCaseKeys<GetOrderList200>['values'][number]
export type Order = CamelCaseKeys<GetOrder200>
// 会計URLのトークン取得(orders#token_show)の表示専用レスポンス。所有者スコープの Order と
// 表示に使うフィールドは同形のため、order state(Order)へそのまま格納できる。
export type OrderByToken = CamelCaseKeys<GetOrderByToken200>
export type SearchedOrder = CamelCaseKeys<GetOrderList200> & {
  condition?: Condition
}

export interface Condition {
  page: number
  per: number
  status: NonNullable<GetOrderListQueryParams['status']>
  issuedUserData: NonNullable<GetOrderListQueryParams['issued_user_data']>
}

export interface OrderSearchCondition {
  page?: number
  per?: number
}

export interface OrderRequest extends OrderBase {
  type: 'order' | 'subscriptionRequest'
}
export interface SubscriptionRequestRequest extends SubscriptionRequestBase {
  type: 'order' | 'subscriptionRequest'
}

export interface Request {
  values: [OrderRequest | SubscriptionRequestRequest]
}

const order = ref(storage<Order>('order').getItem()) as Ref<Order>
watchEffect(() => storage<Order>('order').setItem(order.value))

// footerで使用するため状態管理が必要。
const requests = ref(storage<Request>('requests').getItem()) as Ref<Request>
watchEffect(() => storage<Request>('requests').setItem(requests.value))

const getRequests = async () => {
  // 2つの取得したリソースを、時系列が正しい状態でマージする。
  // typeの設定もここでする
  const orders = ((await searchList({ per: 500 })).values ?? []) as OrderBase[]
  const SubscriptionRequests =
    (await useSubscriptionRequest().searchList()) as SubscriptionRequestBase[]
  const values = [
    ...orders.map((value) => {
      return {
        ...value,
        type: 'order',
      } as OrderRequest
    }),
    ...SubscriptionRequests.map((value) => {
      return {
        ...value,
        type: 'subscriptionRequest',
      } as SubscriptionRequestRequest
    }),
  ].sort((a, b) => (dayjs(a.createdAt).isBefore(dayjs(b.createdAt)) ? 1 : -1))

  requests.value = { values } as Request
  return readonly(requests) as Readonly<Ref<Request>>
}

// index は単一 target スコープ(target 未指定だと本人分しか返らない)。
// 本人+メンバーの会計依頼を全て表示するため、対象者ごとに並列取得して id でマージする。
const searchList = async (condition?: OrderSearchCondition): Promise<SearchedOrder> => {
  const targets = await useSubProfile().getTargets()
  const results = await Promise.all(
    targets.map((target) => searchOrders(condition, target.proxyGroupMemberId))
  )
  const values = mergeById(results.map((r) => (r.values ?? []) as OrderBase[]))
  return { ...results[0], values } as SearchedOrder
}

const getDetail = async (id: number, targetGroupMemberId?: number) => {
  order.value = await getOrderDetail(id, targetGroupMemberId)
  return readonly(order)
}

// 会計URLのトークンで表示専用に取得し order state へ格納する。新規患者(glen未リンク)で
// 未所有の会計でも、所有者スコープ取得(getDetail)のように 404 にならず表示できる。
const getDetailByToken = async (id: number, orderLinkToken: string) => {
  order.value = (await getOrderByToken(id, orderLinkToken)) as Order
  return readonly(order)
}

const getAttachedFile = async (orderId: number, targetGroupMemberId?: number) => {
  return await getOrderAttachedFile(orderId, targetGroupMemberId)
}

// 会計URLのトークンで添付資料を取得する。未所有(対象者選択前)の会計でも 404 にならず、
// 会計本体の表示(getDetailByToken)と同じ認可で取得できる。
const getAttachedFileByToken = async (orderId: number, orderLinkToken: string) => {
  return await getOrderAttachedFileByToken(orderId, orderLinkToken)
}

const getReceiptFile = async (orderId: number, targetGroupMemberId?: number) => {
  return await getOrderReceiptFile(orderId, targetGroupMemberId)
}

const link = async (orderId: number, linkToken: string, targetGroupMemberId?: number) => {
  return (await linkOrder(orderId, linkToken, targetGroupMemberId)) as Order
}

const getTotalAmountFromRequest = (request: OrderBase | SubscriptionRequestBase) => {
  const object = request as any
  return object.totalAmount ?? object.everyTotalAmount
}

const clearOrder = () => {
  order.value = {} as Order
  storage<Order>('order').removeItem()
}

/**
 * 代理操作の宛先不一致(アカウント間違い)エラーかどうかを判定する純関数。
 * - 403 + code: E_ACCOUNT_MISMATCH(業務固有の宛先不一致)
 * - 403 + code なし(汎用の宛先ガード)
 * どちらも pay-error(再ログイン促し)へ寄せる。
 */
export const isAccountMismatchError = (error: unknown): boolean => {
  const e = error as { statusCode?: number; data?: { code?: string } } | null
  return e?.statusCode === 403
}

export const useOrder = () => {
  return {
    order: readonly(order) as Readonly<Ref<Order>>,
    requests: readonly(requests) as Readonly<Ref<Request>>,
    getRequests,
    searchList,
    getDetail,
    getDetailByToken,
    getAttachedFile,
    getAttachedFileByToken,
    getReceiptFile,
    link,
    getTotalAmountFromRequest,
    clearOrder,
  }
}
